import { useState, useEffect } from 'react';
import { getUserGames, getGameById } from '../services/gameService';

interface UserGame {
  id: string;
  game_type: string;
  difficulty: string;
  status: string;
  reaction: string;
  current_prompt: string;
  score: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  history?: Array<{
    action: string;
    response: {
      status: string;
      reaction: string;
      feedback: string;
      next_prompt: string;
      score_adjustment: number;
      suggestions: string[];
      response_quality: string;
    };
    timestamp: string;
    score_change: number;
  }>;
}

export const useUserGames = () => {
  const [games, setGames] = useState<UserGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        setIsLoading(true);
        const data = await getUserGames();
        setGames(data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch user games');
        console.error('Error in useUserGames:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGames();
  }, []);

  return { games, isLoading, error };
};

export const useGameDetails = (gameId: string | null) => {
  const [game, setGame] = useState<UserGame | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId) return;

    const fetchGameDetails = async () => {
      try {
        setIsLoading(true);
        const data = await getGameById(gameId);
        setGame(data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch game details');
        console.error('Error in useGameDetails:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGameDetails();
  }, [gameId]);

  return { game, isLoading, error };
}; 